/*
 * Copyright (c) Arc XP 2025.
 */

// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../node_modules/mux-embed/dist/types/mux-embed.d.ts"/>
import mux from 'mux-embed';
import { KoiEventBus } from './koi-event-bus';
import type { AnalyticsEnvironment } from './types';

/**
 * The KoiAnalytics class is responsible for initializing the third-party video analytics plugin(s).
 *
 * KoiAnalytics attaches the analytics to the video/audio element that is created by the KoiPlayer,
 * as well as doing sampled activation.
 */
export class KoiAnalytics {
  /** The configuration object for the analytics system. */
  config: AnalyticsConfig;

  /**
   * Constructs a new KoiAnalytics instance.
   *
   * @param config - Analytics configuration object.
   */
  constructor(config: AnalyticsConfig) {
    this.config = config;
  }

  /**
   * Initializes the media monitoring for the given media element.
   *
   * @returns This method does not return a value.
   */
  init(): void {
    const sampleRate = this.config.meta.sampleRate || 0;
    const sampleKey = Math.random();
    if (sampleRate < sampleKey) {
      this._dispatchSkippedEvent('sampleRate is less than random value');
      return;
    }

    if (!this.config.mediaElement) {
      this._dispatchSkippedEvent('mediaElement is null');
      return;
    }

    if (this.config.mediaId === undefined) {
      this._dispatchSkippedEvent('mediaId is null');
      return;
    }

    mux.monitor(this.config.mediaElement, {
      debug: this.config.debug || false,
      data: {
        env_key: this.getPublicKey(this.config.env),
        player_name: 'koi-player',
        player_init_time: this.config.player_init_time,
        sub_property_id: this.config.orgId,
        video_id: this.config.mediaId,
        video_content_type: this.config.type,
        video_title: this.config.meta.title,
        custom_3: String(sampleRate),
      },
    });
  }

  /**
   * Dispatches an analytics-skipped event with the provided reason.
   *
   * @param reason - The reason why analytics was skipped.
   */
  private _dispatchSkippedEvent(reason: string): void {
    const eventBus = KoiEventBus.getInstance();
    eventBus.dispatchEvent(
      new CustomEvent('koi-analytics-skipped', {
        detail: {
          mediaId: this.config.mediaId,
          reason,
        },
      })
    );
  }

  /**
   * Retrieves the public key associated with the specified analytics environment.
   *
   * @param env - The analytics environment for which the public key is being retrieved. Can be
   *   'prod' for production or 'sandbox' for sandbox environment.
   * @returns The public key for the specified environment, or an empty string if the environment is
   *   invalid.
   */
  private getPublicKey(env: AnalyticsEnvironment) {
    if (env === 'prod') {
      return ANALYTICS_PUBLIC_KEYS.PROD;
    } else if (env === 'sandbox') {
      return ANALYTICS_PUBLIC_KEYS.SANDBOX;
    }
    return '';
  }
}

const ANALYTICS_PUBLIC_KEYS = {
  SANDBOX: 'ec491ce5cf6d7bfd2184d4b7f',
  PROD: '1ef80c94c65fbc10d54a7ca3c',
};

export interface AnalyticsConfig {
  mediaElement: HTMLMediaElement;
  player_init_time: number;
  orgId: string;
  env: AnalyticsEnvironment;
  mediaId: string;
  type: string;
  meta: {
    title?: string;
    sampleRate?: number;
    ready?: boolean;
  };
  debug?: boolean;
}
